import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle
} from '@mui/material'
import { ActionRequest, Device } from '../types'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../hooks'
import { ProductType } from '../enums'
import powerSupplyService from '../services/powerSupplyService'
import { useDispatch } from 'react-redux'
import { updatePowerSupply } from '../redux/slices/powerSupplies'
import { createSuccessNotification, createWarningNotification } from '../redux/slices/notifications'
import { createUserNotification } from '../utils/createUserNotification'
import { PowerSupplyErrors } from '../enums/PowerSupplyError'
import { useNavigate } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { getActionRequestsByProject } from '../services/projectService'
import { RequesterType } from '../enums/RequesterType'
import LoadingState from './LoadingState'
import { LoadingButton } from '@mui/lab'
import { RequestStatus } from '../enums/RequestStatus'
import { updateRequestState } from '../services/requestService'
import { deleteDeviceLocationChangeRequest } from '../redux/slices/requests'

export interface DeviceDetachDialogProps extends Omit<DialogProps, 'onClose' | 'children'> {
  /**
   * The device
   */
  device: Device

  /**
   * Handle the dialog close event.
   */
  onClose: () => void

  /**
   * Handle the after submit event.
   */
  afterSubmit?: (device: Device) => void
}

export default function DeviceDetachDialog({
  device,
  open,
  onClose,
  afterSubmit,
  ...props
}: DeviceDetachDialogProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('common')

  /**
   * The dispatch function.
   */
  const dispatch = useDispatch()

  /**
   * The navigate function.
   */
  const navigate = useNavigate()

  /**
   * The auth state.
   */
  const { currentUser } = useAuth()

  /**
   * The loading state.
   */
  const [loading, setLoading] = useState<boolean>(true)

  /**
   * The submitting state.
   */
  const [submitting, setSubmitting] = useState<boolean>(false)

  /**
   * The action requests of device.
   */
  const [actionRequests, setActionRequests] = useState<ActionRequest[]>([])

  /**
   * Approve the action requests.
   */
  const approveActionRequests = async () => {
    for (const actionRequest of actionRequests) {
      const updatedActionRequest = await updateRequestState(actionRequest.id, {
        status: RequestStatus.APPROVED,
        approver: `${currentUser?.firstName} ${currentUser?.lastName}`
      })

      dispatch(deleteDeviceLocationChangeRequest(updatedActionRequest.id))
    }
  }

  /**
   * Handle the confirm action.
   */
  const handleConfirm = async () => {
    try {
      if (device?.powerSupply && device?.asset?.project) {
        setSubmitting(true)

        const inputCables =
          device?.product?.type === ProductType.PLUG_AND_PLAY
            ? 0
            : (device.powerSupply.inputCables ?? 0)

        const fuse =
          device?.product?.type === ProductType.PLUG_AND_PLAY ? 0 : (device.powerSupply.fuse ?? 0)

        const updatedPowerSupply = await powerSupplyService.updatePowerSupply(
          device.powerSupply.id,
          {
            name: device.powerSupply.name,
            description: device.powerSupply.description,
            fuse,
            inputCables,
            voltage_1_tp: device.powerSupply.voltage_1_tp,
            voltage_1_ts: device.powerSupply.voltage_1_ts,
            isMainDistributionAssembly: device.powerSupply.isMainDistributionAssembly,
            transformCurrent: device.powerSupply.transformCurrent,
            customerReference: device.powerSupply.customerReference,
            current_1_offset: device.powerSupply.current_1_offset,
            current_2_offset: device.powerSupply.current_2_offset,
            current_3_offset: device.powerSupply.current_3_offset,
            voltage_1_offset: device.powerSupply.voltage_1_offset,
            voltage_2_offset: device.powerSupply.voltage_2_offset,
            voltage_3_offset: device.powerSupply.voltage_3_offset,
            project: null,
            parent: null,
            lift: null,
            level: null
          }
        )

        dispatch(updatePowerSupply(updatedPowerSupply))

        // Approve the action requests of device.
        await approveActionRequests()

        dispatch(createSuccessNotification(t('deviceDetachedFromTheProjectSuccess')))

        setSubmitting(false)

        afterSubmit?.(device)
      }
    } catch (error) {
      setSubmitting(false)

      const errorMessage = createUserNotification({
        user: currentUser,
        type: PowerSupplyErrors.EDIT,
        error: error
      })

      dispatch(createWarningNotification(t(errorMessage?.key) + t(errorMessage?.message)))
    }
  }

  /**
   * Load the action requests.
   */
  async function loadActionRequests() {
    try {
      setLoading(true)

      if (device?.asset?.projectId) {
        setActionRequests(
          await getActionRequestsByProject(device.asset.projectId, {
            requesterType: RequesterType.DEVICE,
            requesterId: device?.name
          })
        )
      }

      setLoading(false)
    } catch (_error) {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadActionRequests()
  }, [device])

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth {...props}>
      <DialogTitle>{t('detachFromProject')}</DialogTitle>

      <DialogContent>
        {loading ? (
          <LoadingState />
        ) : (
          <Alert severity="warning">{t('confirmDeviceDetachText')}</Alert>
        )}
      </DialogContent>

      {!loading && (
        <DialogActions>
          <Button variant="text" color="inherit" onClick={onClose} autoFocus>
            {t('cancel')}
          </Button>

          <LoadingButton
            variant="contained"
            color="warning"
            loading={submitting}
            onClick={handleConfirm}
          >
            {t('confirm')}
          </LoadingButton>
        </DialogActions>
      )}
    </Dialog>
  )
}
